import type { IconButtonProps } from '@mui/material/IconButton';

import { useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import MenuList from '@mui/material/MenuList';
import IconButton from '@mui/material/IconButton';
import MenuItem, { menuItemClasses } from '@mui/material/MenuItem';
import { Settings as SettingsIcon } from '@mui/icons-material';

import { useLocation, useNavigate } from 'react-router';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import { getUserEntity } from 'common/UserEntityProvider';

// ----------------------------------------------------------------------

export type SettingsPopoverProps = IconButtonProps & {
  data?: {
    label: string;
    href: string;
    icon?: React.ReactNode;
    viewScopes?: string[];
    editScopes?: string[];
    viewRoles?: string[];
    editRoles?: string[];
    entityType?: number;
  }[];
};

export function SettingsPopover({ data = [], sx, ...other }: SettingsPopoverProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const userEntity = getUserEntity();

  const [openPopover, setOpenPopover] = useState<HTMLButtonElement | null>(null);

  const handleOpenPopover = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenPopover(event.currentTarget);
  }, []);

  const handleClosePopover = useCallback(() => {
    setOpenPopover(null);
  }, []);

  const handleClickItem = useCallback(
    (path: string) => {
      handleClosePopover();
      navigate(path);
    },
    [handleClosePopover, navigate]
  );

  return (
    <>
      <IconButton
        onClick={handleOpenPopover}
        sx={{
          width: 40,
          height: 40,
          ...sx,
        }}
        {...other}
      >
        <SettingsIcon />
      </IconButton>

      <Popover
        open={!!openPopover}
        anchorEl={openPopover}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { width: 250 },
          },
        }}
      >
        <MenuList
          disablePadding
          sx={{
            p: 1,
            gap: 0.5,
            display: 'flex',
            flexDirection: 'column',
            [`& .${menuItemClasses.root}`]: {
              px: 1,
              gap: 2,
              borderRadius: 0.75,
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
              [`&.${menuItemClasses.selected}`]: {
                color: 'text.primary',
                bgcolor: 'action.selected',
                fontWeight: 'fontWeightSemiBold',
              },
            },
          }}
        >
          {data.map((option) => {
            const isAllowed = 
              !option.entityType || 
              option.entityType === userEntity?.entityType;

            if (!isAllowed) return null;

            return (
              <PermissionsGate
                key={option.label}
                viewScopes={option.viewScopes || []}
                editScopes={option.editScopes || []}
                viewRoles={option.viewRoles || []}
                editRoles={option.editRoles || []}
                entityType={option.entityType}
              >
                <MenuItem
                  selected={option.href === pathname}
                  onClick={() => handleClickItem(option.href)}
                >
                  {option.icon && <Box component="span">{option.icon}</Box>}
                  {option.label}
                </MenuItem>
              </PermissionsGate>
            );
          })}
        </MenuList>
      </Popover>
    </>
  );
}
